<template>
	<div class="width-full relative">
		<label :for="name" class="width-full">
			<div v-if="$slots.customLabel" class="pb-2">
				<slot name="customLabel" />
			</div>
			<span
				v-else
				class="text-body-m text-neutral-900 pb-2"
				:style="{ display: label ? 'block' : 'none' }"
			>
				{{ label }}
				<span v-if="required" class="text-error-500">*</span>
			</span>
			<div :class="$slots.selector ? 'd-flex' : ''">
				<slot name="selector" :border-class="borderClasses" />
				<div
					class="width-full d-flex align-center border-xs px-4 py-3 bg-white justify-space-between"
					:class="[
						borderClasses,
						disabled ? 'bg-neutral-50' : 'cursor-pointer',
						$slots.selector ? 'rounded-r-2' : 'rounded-2',
					]"
					:style="{ minHeight: height }"
					ref="inputWrapper"
					tabindex="-1"
					@mousedown="onMouseDown"
					@focusin="onWrapperFocusIn"
					@focusout="onWrapperFocusOut"
				>
					<div class="width-full d-flex align-center">
						<slot name="prefix-icon" />
						<div class="width-full d-flex align-center">
							<span
								v-if="prefix"
								class="pr-1"
								:class="error ? 'text-error-500' : 'text-neutral-500'"
							>
								{{ prefix }}
							</span>
							<textarea
								v-if="textArea"
								ref="input"
								v-model="inputValue"
								:name="name"
								:id="name"
								:placeholder="placeholder"
								:readonly="readonly"
								:disabled="disabled"
								rows="1"
								:class="{ 'bg-neutral-50': disabled }"
								class="width-full text-neutral-900 d-flex flex-grow-1 width-12 resize-none overflow-hidden bg-transparent"
								style="font-weight: inherit; font-size: inherit; font-family: inherit"
								@keydown="handleTextAreaKeydown"
								@keyup="handleTextAreaKeyup"
								@blur="emit('blur')"
								@keyup.enter="$emit('enter')"
							/>
							<input
								v-else
								ref="input"
								v-model="inputValue"
								:name="name"
								:id="name"
								:placeholder="placeholder"
								:type="type"
								:autocomplete="autocomplete"
								:readonly="readonly"
								:disabled="disabled"
								:min="min"
								:max="max"
								:class="[disabled ? 'text-neutral-500' : 'text-neutral-900']"
								class="width-full d-flex flex-grow-1 width-12 bg-transparent"
								style="font-weight: inherit; font-size: inherit; font-family: inherit"
								@keydown="emit('keydown')"
								@keyup="emit('keyup')"
								@blur="emit('blur')"
								@keyup.enter="$emit('enter')"
								@click="$emit('click')"
							/>
							<span
								v-if="suffix"
								class="pr-1"
								:class="error ? 'text-error-500' : 'text-neutral-500'"
							>
								{{ suffix }}
							</span>
						</div>
					</div>
					<div class="justify-self-end">
						<slot name="suffix-icon" />
					</div>
				</div>
			</div>
		</label>
		<template v-if="!hideErrorBox">
			<div class="help-text-area text-body-s">
				<p v-if="error" class="text-error-500">{{ error }}</p>
				<p v-else-if="helperText" class="text-neutral-600">{{ helperText }}</p>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, watch, nextTick } from 'vue'

	export type FocusableInput = HTMLInputElement & { focusInput: () => void }

	interface Props {
		name?: string
		label?: string
		placeholder?: string
		hideErrorBox?: boolean
		error?: string
		type?: 'text' | 'password' | 'number'
		size?: 's' | 'm' | 'l'
		prefix?: string
		suffix?: string
		readonly?: boolean
		textArea?: boolean
		preventEnter?: boolean
		autocomplete?: string
		required?: boolean
		disabled?: boolean
		helperText?: string
		min?: number
		max?: number
	}

	const props = withDefaults(defineProps<Props>(), {
		name: '',
		label: '',
		placeholder: '',
		hideErrorBox: false,
		error: '',
		type: 'text',
		size: 'm',
		prefix: '',
		suffix: '',
		readonly: false,
		textArea: false,
		preventEnter: false,
		autocomplete: undefined,
		required: false,
		helperText: '',
		min: undefined,
		max: undefined,
	})
	const emit = defineEmits(['click', 'focus', 'focusout', 'keydown', 'keyup', 'enter', 'blur'])

	const input = ref<HTMLElement>()
	const inputValue = defineModel<string | number>()
	const isFocused = ref<boolean>(false)
	const inputWrapper = ref<HTMLElement>()

	const focusInput = () => {
		input.value?.focus()
		emit('click')
	}

	const borderClasses = computed(() => {
		const errorBorder = ['border-error-500', 'input-text-error']
		const focusedBorder = ['border-neutral-700']
		const regularBorder = ['border-neutral-200']

		if (props.error) return errorBorder
		if (props.readonly) return regularBorder
		if (isFocused.value) return focusedBorder

		return regularBorder
	})

	const height = computed(() => {
		const heights = {
			s: '44px',
			m: '64px',
			l: '130px',
		}
		return heights[props.size]
	})

	const resizeTextarea = () => {
		if (!inputValue.value) return
		if (input.value instanceof HTMLTextAreaElement) {
			input.value.style.height = 'auto'
			input.value.style.height = input.value.scrollHeight + 'px'
		}
	}

	watch(
		inputValue,
		() => {
			if (props.textArea) nextTick(resizeTextarea)
		},
		{ immediate: true }
	)

	defineExpose({ focusInput })

	const handleTextAreaKeydown = (event: KeyboardEvent) => {
		if (props.preventEnter && event.key === 'Enter') {
			event.preventDefault()
		}
		emit('keydown')
	}

	const handleTextAreaKeyup = (event: KeyboardEvent) => {
		if (props.preventEnter && event.key === 'Enter') {
			event.preventDefault()
		}
		emit('keyup')
	}

	const onWrapperFocusIn = () => {
		if (isFocused.value) return
		isFocused.value = true
		emit('focus')
	}

	const onWrapperFocusOut = (event: FocusEvent) => {
		const nextTarget = event.relatedTarget as Node
		const hasFocusedChild = nextTarget && inputWrapper.value?.contains(nextTarget)
		if (hasFocusedChild) return
		isFocused.value = false
		emit('focusout')
	}

	const onMouseDown = (event: MouseEvent) => {
		if (props.disabled) return
		const target = event.target as HTMLElement | null
		if (target?.closest('input, textarea')) return
		event.preventDefault()
		focusInput()
	}
</script>

<style scoped lang="scss">
	input,
	textarea {
		border: none;
	}
	textarea {
		border: none;
		resize: none;
	}
	input::placeholder,
	textarea::placeholder {
		opacity: 1; /* Firefox */
		color: var(--color-neutral-500);
	}
	input:focus,
	textarea:focus {
		outline: none;
		&::placeholder {
			color: var(--color-neutral-400);
		}
	}

	.input-text:hover {
		&:not(.input-text-error) {
			border-color: var(--color-neutral-500) !important;
		}
		input::placeholder {
			color: var(--color-neutral-400);
		}
	}

	.help-text-area {
		height: 20px;
		margin-top: 2px;
	}

	/* Chrome, Safari, Edge, Opera */
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button,
	textarea::-webkit-outer-spin-button,
	textarea::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	/* Firefox */
	input[type='number'] {
		-moz-appearance: textfield;
	}
</style>
